import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Form, InputGroup, OverlayTrigger, Popover, Table, Tooltip } from "react-bootstrap";
import { useAppSelector } from "../../redux/hooks";
import { navigatePopupPage } from "../../compatManager";
import { lddGlobalSessionInfo } from "../navigationTypes";
import { NavigationContext } from "../../Context/NavigationContext";
import { Document, Tag, Creator, User, Custom, creators, PSM, P, S, M, psm, Category, Package, CompoundDocument } from "./types/Types";
import { DocMastersFilter } from "./Filter";
import { getDocumentList, navDocEditor } from "./DocumentsFuncs";
import Loading from "../Loading";

interface IDocMastersProps {
    screenType?: "DocMasters" | "Packages" | "PackageEditor";
    newPkg?: Package;
    setNewPkg?: (pkg: Package) => void;
}

const DocMasters: FC<IDocMastersProps> = (props) => {
    const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
    const nav = useContext(NavigationContext);

    const screenType = props.screenType?? "DocMasters";
    const [docs, setDocs] = useState<Document[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [searchDocText, setSearchDocText] = useState<string>('');
    const [debouncedSearchDocText, setDebouncedSearchDocText] = useState<string>('');
    const [filtersSelect, setFiltersSelect] = useState<Array<Tag | PSM | Creator | Category | Package>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        async function initDocList() {
            try {
                const {docList, categoryList} = await getDocumentList(SESSION_INFO.appProvinceShort, SESSION_INFO.firm.id.toString(), SESSION_INFO.sid);
                setDocs(docList);
                setCategories(categoryList);
            } catch (error) {
                console.error("Error loading documents: ", error);
            } finally {
                setIsLoading(false);
            }
        }
        setIsLoading(true);
        initDocList();

        // if in package editor mode, show docs of PSM same as the new package by default
        if (screenType === "PackageEditor" && props.newPkg?.tags?.some((tag) => psm.includes(tag)))
            setFiltersSelect([props.newPkg.tags.find((tag) => psm.includes(tag)) as PSM]);
    }, []);

    const docsFiltered = docs.filter((doc) => {
        let isSearchMatch = true;
        if (searchDocText !== '' && !doc.name.toLowerCase().includes(searchDocText.toLowerCase()))
            isSearchMatch = false;
        
        // each doc has all its tags in doc.tags that could possibly be applicable to the filter
        // all we care about is whether the filterSelect is a subset of doc.tags
        let isFilterMatch = true;
        filtersSelect.forEach(tag => {
            if (!doc.tags.some((t) => t === tag))
                isFilterMatch = false;
        });

        return isSearchMatch && isFilterMatch;
    });

    // update search text 500ms after users stop typing
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearchDocText(debouncedSearchDocText);
        }, 500);
        return () => {
          clearTimeout(handler);
        };
      }, [debouncedSearchDocText]);

    const handleFilterClick = useCallback((tag: Tag | Category | Package) => {
        if (filtersSelect.includes(tag)) {
            setFiltersSelect(filtersSelect.filter((t) => t !== tag));
        } else {
            setFiltersSelect([...filtersSelect, tag]);
        }
    }, [filtersSelect, setFiltersSelect]);

    const handleAddNewDoc = () => {
        const ret = navigatePopupPage("addnewDocumentWizard.asp?SessionID=" + SESSION_INFO.sid, null, true);
        ret.then((docInfo) => {
            const newDoc: Document = {
                name: docInfo[2],
                doc_id: docInfo[0],
                doc_type_id: docInfo[1],
                category_id: docInfo[3],
                template_type_id: docInfo[4],
                tags: [User],
            };
            navDocEditor(newDoc, SESSION_INFO, nav);
        });
    }

    const filterPopover = (
        <Popover>
            <DocMastersFilter
                tags={categories}
                filtersSelect={filtersSelect}
                setFiltersSelect={setFiltersSelect}
                screenType={screenType}
            />
        </Popover>
    );

    return (
        <>
            {isLoading && <Loading showMsg={true} extraMsg="Loading Documents" inline={true} />}
            
            <Card className="card-list card-search" style={{height:'100%', marginBottom: '0'}}>

                {screenType === "PackageEditor" && (
                    <div className="alert-area">
                        <Alert variant="info">Click a document to add it to a package.</Alert>
                    </div>
                )}
                
                <Card.Header>
                    {screenType !== "PackageEditor" && (
                        <h2>Document Masters</h2>
                    )}
                    <InputGroup className="search-bar">
                        <InputGroup.Text>
                            <i style={{ cursor: "default" }} className="fa-duotone fa-magnifying-glass fa-indicator"/>
                        </InputGroup.Text>
                        <Form.Control
                            type="text" 
                            placeholder="Type to search for a document..." 
                            onChange={(e) => setDebouncedSearchDocText(e.target.value)} 
                            value={debouncedSearchDocText}
                        />

                        <div className="buttons-container">
                            {debouncedSearchDocText > '' && (
                                <OverlayTrigger key="clearSearchButton" placement="bottom" delay={200} overlay={<Tooltip>Clear search</Tooltip>}>
                                    <Button variant="link" id="clearSearchBtn" onClick={() => {
                                        if (debouncedSearchDocText !== '')
                                            setDebouncedSearchDocText('');
                                    }}>
                                        <i id="clearSearchBtn2" className="fa-light fa-xmark"/>
                                    </Button>
                                </OverlayTrigger>
                            )}

                            {(filtersSelect.filter((tag) => tag).length > 0) && (
                                <>
                                    {/* only show root category filters that selected */}
                                    {filtersSelect.filter((tag) => (tag && (tag.parentNames? !tag.parentNames.some((prtNm) => (filtersSelect.some((f) => f.name === prtNm))) : true))).map((tag) => (
                                        <OverlayTrigger key={`${tag.name}`} placement="bottom" delay={200} overlay={<Tooltip >Remove tag</Tooltip>}>
                                            <Badge className="tag removable" pill bg="info" key={tag.name} onClick={() => handleFilterClick(tag)}><span className="text-truncate" style={{maxWidth: '100px'}}>{'alias' in tag? tag.alias : tag.name}</span></Badge>
                                        </OverlayTrigger>
                                    ))}
                                </>
                            )}

                            <OverlayTrigger 
                                key="openFilterPopoverButton" 
                                trigger="click"
                                placement="bottom"
                                overlay={filterPopover}
                                rootClose
                            >
                                <Button variant="link"><i className="fa-regular fa-bars-filter"/>Filter</Button>
                            </OverlayTrigger>
                        </div>
                    </InputGroup>

                    {screenType === "DocMasters" && (
                        <OverlayTrigger key="addNewDocButton" placement="bottom" delay={200} overlay={<Tooltip >Add new master</Tooltip>}>
                            <Button variant="primary" onClick={() => handleAddNewDoc()}><i className="fa-solid fa-plus"/>Document</Button>
                        </OverlayTrigger>
                    )}
                </Card.Header>

                <Card.Body>
                    <Table hover style={{tableLayout: 'fixed'}}>
                        <thead>
                            {screenType === "PackageEditor" ? (
                                <tr>
                                    <th>Document</th>
                                    <th style={{ width: '80px' }}>File Type</th>
                                    <th style={{ width: '30px' }}></th>
                                </tr>
                            ) : (
                                <tr>
                                    <th style={{ width: '55%' }}>Document</th>
                                    <th style={{ width: '90px' }}>File Type</th>
                                    <th style={{ width: '80px' }}>Created</th>
                                    <th>Categories</th>
                                </tr>
                            )}
                        </thead>

                        <tbody>
                            {docsFiltered.sort((a,b) => {
                                // sort by alphabetical order, but special characters at the end
                                const aIsSpecial = /^[^a-zA-Z0-9]/.test(a.name);
                                const bIsSpecial = /^[^a-zA-Z0-9]/.test(b.name);
                                if (aIsSpecial && !bIsSpecial) return 1
                                else if (!aIsSpecial && bIsSpecial) return -1
                                else return a.name.localeCompare(b.name);
                            }).map((doc) => (
                                <tr key={doc.doc_id} onClick={() => {
                                    if(screenType === "PackageEditor") {
                                        props.setNewPkg({
                                            ...props.newPkg,
                                            doc_list: [...props.newPkg.doc_list, doc]
                                        });
                                    }
                                }}>
                                    
                                    <OverlayTrigger placement="top" delay={300} overlay={
                                        <Tooltip>{doc.name}</Tooltip>
                                    }>
                                        <td className="nowrap text-truncate" onClick={() => {
                                            if (screenType === "DocMasters") {
                                                if (doc.is_uneditable) {
                                                    alert("This document is not editable.");
                                                } else {
                                                    navDocEditor(doc, SESSION_INFO, nav);
                                                }
                                            }
                                        }}>
                                            {doc.template_type_id === 10 ? (
                                                <i className="fa-solid fa-file-pdf status-pdf" style={{marginRight: '5px'}}></i>
                                            ) : doc.tags?.includes(CompoundDocument) ? (
                                                <i className="fa-solid fa-memo status-compound" style={{marginRight: '5px'}}></i>
                                            ) : (
                                                <i className="fa-solid fa-file-word status-word" style={{marginRight: '5px'}}></i>
                                            )}
                                            {doc.name}
                                        </td>
                                    </OverlayTrigger>

                                    <td>
                                        <div className="badge-file-container">
                                            <Badge bg={doc.tags.includes(P) ? "info" : "secondary"} text="dark">P</Badge>
                                            <Badge bg={doc.tags.includes(S) ? "info" : "secondary"} text="dark">S</Badge>
                                            <Badge bg={doc.tags.includes(M) ? "info" : "secondary"} text="dark">M</Badge>
                                        </div>
                                    </td>

                                    {screenType === "DocMasters" && (
                                        <>
                                            <td>
                                                {doc.tags.includes(User) ? (
                                                    <Badge bg="success" text="dark" pill>User</Badge>
                                                ) : doc.tags.includes(Custom) ? (
                                                    <Badge bg="custom" text="dark" pill>Custom</Badge>
                                                ) : (
                                                    <Badge bg="system" text="dark" pill>System</Badge>
                                                )}
                                            </td>
                                            <td>
                                                {doc.tags.filter((tag) => (tag && ![...psm, ...creators].some((t) => t === tag) && (tag.children? !tag.children.some((chd) => (doc.tags.includes(chd))) : true))).map((tag, index) => (
                                                    <Badge key={`tag-${doc.doc_id}-${tag.name}-${index}`} bg="info" text="dark" pill>{tag.name}</Badge>
                                                ))}
                                            </td>
                                        </>
                                    )}
                                    {screenType === "PackageEditor" && (
                                        <td><i className="fa-regular fa-circle-arrow-right"/></td>
                                    )}
                                </tr>
                            ))}

                            {docs.length > 0 && (
                                <tr key={'EndMsg'}>
                                    <td colSpan={screenType === "PackageEditor"? 3 : 4}>
                                        <Alert variant="primary">{docsFiltered.length === 0 ? "- No Search Result found -" : "- End of Results -"}</Alert>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </>
    );
}

export default DocMasters;